import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faBars, faXmark } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './Logo';
import CartDropdown from './CartDropdown';
import { useUI } from '../context/UIContext';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';
import LanguageSwitcher from './LanguageSwitcher';

const Header: React.FC = () => {
  const { isCartOpen, setIsCartOpen } = useUI();
  const { items } = useCart();
  const { t } = useTranslation();
  const { localePath } = useLocale();
  // Calculate total quantity of items in cart
  const totalCartQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const cartRef = useRef<HTMLDivElement>(null);
  const cartButtonRef = useRef<HTMLButtonElement>(null);


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is within cart area or its interactive elements
      const isCartArea = cartRef.current?.contains(target) ||
                        cartButtonRef.current?.contains(target) ||
                        target.closest('.cart-dropdown') ||
                        target.closest('.quantity-controls') ||
                        target.closest('.remove-button');
      // Check for navigation elements
      const isCheckoutButton = target.closest('.checkout-button');
      const isProductLink = target.closest('.product-link');

      // Close if clicking outside cart area OR clicking navigation elements
      if (isCartOpen && (!isCartArea || isCheckoutButton || isProductLink)) {
        setIsCartOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCartOpen]);

  // Handle body overflow
  useEffect(() => {
    if ((isCartOpen || isOpen) && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCartOpen, isOpen, isMobile]);

  const menuVariants = {
    closed: { opacity: 0, x: "100%" },
    open: { opacity: 1, x: 0 }
  };

  return (
    <header className="dark:text-white p-4 my-2 top-0 z-50 container">
      <div className="mx-auto flex justify-between items-center md:w-screen md:px-20">
        <motion.div whileHover={{ scale: 1.1 }}>
          <Logo />
        </motion.div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-4 items-center relative z-50">
          <motion.div whileHover={{ scale: 1.1 }}>
            <Link to={localePath('/shop')} className="transition-transform block">{t('nav.shop')}</Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.1 }}>
            <Link to={localePath('/about')} className="transition-transform block">{t('nav.about')}</Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.1 }}>
            <Link to={localePath('/contact')} className="transition-transform block">{t('nav.contact')}</Link>
          </motion.div>
          <LanguageSwitcher />
          {/* Desktop Cart Button and Dropdown */}
          <div className="relative" ref={cartRef}>
            <motion.button
              ref={cartButtonRef}
              whileHover={{ scale: 1.1 }}
              aria-label={t('nav.cart')}
              className="flex items-center justify-center px-4 py-2 h-10 rounded-lg bg-brand-blue transition-transform text-text-light relative"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={() => setIsCartOpen(!isCartOpen)}
            >
              <FontAwesomeIcon icon={faShoppingCart} size="sm" />
              {totalCartQuantity > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {totalCartQuantity}
                </span>
              )}
            </motion.button>

            <CartDropdown isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
          </div>
        </nav>

        {/* Mobile Controls */}
        <div className="flex items-center gap-4 md:hidden">
          {/* Mobile Cart Button - hide if mobile menu is open */}
          {!isOpen && (
            <div className="relative" ref={cartRef}>
              <button
                ref={cartButtonRef}
                aria-label={t('nav.cart')}
                className="flex items-center justify-center px-4 py-2 h-10 rounded-lg bg-brand-blue text-white relative"
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={() => setIsCartOpen(!isCartOpen)}
              >
                <FontAwesomeIcon icon={faShoppingCart} className="h-5 w-5" />
                {totalCartQuantity > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {totalCartQuantity}
                  </span>
                )}
              </button>
              <CartDropdown isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
            </div>
          )}
          {/* Mobile Hamburger / Close — hide when cart is open */}
          {!isCartOpen && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              className="z-[80] w-10 h-10 flex items-center justify-center rounded-full text-brand-dark dark:text-brand-white transition-colors hover:bg-black/5 dark:hover:bg-white/10"
            >
              <FontAwesomeIcon icon={isOpen ? faXmark : faBars} className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed inset-0 bg-brand-sand dark:bg-brand-charcoal z-[70] md:hidden"
            >
              <div className="flex flex-col items-center justify-center h-full gap-2">
                {/* Logo at top */}
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className="absolute top-6 left-1/2 transform -translate-x-1/2"
                >
                  <Logo />
                </motion.div>

                {/* Navigation Items */}
                {[
                  { to: localePath('/'), label: t('nav.home') },
                  { to: localePath('/shop'), label: t('nav.shop') },
                  { to: localePath('/about'), label: t('nav.about') },
                  { to: localePath('/contact'), label: t('nav.contact') },
                ].map((item, i) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.06, duration: 0.4 }}
                    className="text-center"
                  >
                    <Link
                      to={item.to}
                      onClick={() => setIsOpen(false)}
                      className="text-brand-dark dark:text-brand-white text-3xl font-light tracking-wide py-3 px-8 block transition-colors hover:text-brand-blue dark:hover:text-brand-blue"
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                ))}

                {/* Language Switcher in mobile menu */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.4 }}
                  className="mt-2"
                >
                  <LanguageSwitcher onNavigate={() => setIsOpen(false)} />
                </motion.div>

                {/* Divider */}
                <motion.div
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                  className="w-16 h-px bg-brand-dark/20 dark:bg-white/20 my-2"
                />

                {/* Cart Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.4 }}
                >
                  <Link
                    to={localePath('/cart')}
                    onClick={() => {
                      setIsOpen(false);
                      setIsCartOpen(false);
                    }}
                    className="flex items-center justify-center gap-3 px-10 py-3.5 rounded-full bg-brand-blue text-white text-lg tracking-wide relative transition-colors hover:bg-brand-dark"
                  >
                    <FontAwesomeIcon icon={faShoppingCart} className="text-base" />
                    {t('nav.cart')}
                    {totalCartQuantity > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {totalCartQuantity}
                      </span>
                    )}
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
